import {
  remittanceInvoiceCandidateSchema,
  remittanceMemorySummarySchema,
  remittanceReprocessCountsSchema,
} from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { z } from "zod";
import { listSortShape } from "../../schemas/common";
import { remittanceVerificationResultSchema } from "@erp/shared-types";
import { InboundEmailSchema } from "../accounts-payable/ap-invoices.contract";

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/** The invoice a remittance line resolved to. */
export const RemittanceInvoiceSummarySchema = z.object({
  id: UUIDSchema,
  invoiceNumber: z.string().nullable(),
  invoiceDate: z.string(),
  netAmount: DecimalStringSchema.nullable(),
  balanceDue: DecimalStringSchema.nullable(),
  /** The invoice's billed customer — links `customerName` to its profile. */
  customerId: UUIDSchema.nullable(),
  customerName: z.string().nullable(),
  status: z.string(),
});

/** The cash receipt a remittance is linked to, for display. */
export const RemittanceLinkedReceiptSchema = z.object({
  id: UUIDSchema,
  referenceNumber: z.string().nullable(),
  amount: z.string().nullable(),
  transactionDate: z.string().nullable(),
});

export const RemittanceChannelSchema = z.enum([
  "email",
  "edi",
  "portal",
  "manual",
  "bank_memo",
]);

export const RemittanceMatchStatusSchema = z.enum([
  "unmatched",
  "auto_matched",
  "matched",
  "ambiguous",
  "rejected",
]);

export const RemittanceLineItemKindSchema = z.enum([
  "invoice_payment",
  "deduction",
]);

export const RemittanceReviewFilterSchema = z.enum([
  "needs_review",
  "complete",
  "all",
]);

// ---------------------------------------------------------------------------
// Line item summary (aggregate counts per advice, used in list view)
// ---------------------------------------------------------------------------

export const RemittanceLineItemSummarySchema = z.object({
  total: z.int().nonnegative(),
  matched: z.int().nonnegative(),
  autoMatched: z.int().nonnegative(),
  unmatched: z.int().nonnegative(),
  ambiguous: z.int().nonnegative(),
  rejected: z.int().nonnegative(),
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

/**
 * Columns the remittances list can be sorted by — each is a column id of the
 * Remittances table. The line-item summary is not here: it is a set of counts
 * over the remittance's line items.
 */
export const RemittanceSortBySchema = z.enum([
  "receivedAt",
  "statedTotalAmount",
  "statedCustomerName",
  "channel",
  "linkedReceipt",
]);

export const ListRemittancesInputSchema = z.object({
  reviewFilter: RemittanceReviewFilterSchema.optional(),
  channel: z.array(RemittanceChannelSchema).optional(),
  receivedDateFrom: z.string().optional(),
  receivedDateTo: z.string().optional(),
  statedTotalAmountMin: z.number().optional(),
  statedTotalAmountMax: z.number().optional(),
  customerId: z.array(UUIDSchema).optional(),
  search: z.string().optional(),
  ...listSortShape(RemittanceSortBySchema),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

// Forward-declared inline (instead of using RemittanceMatchedCustomerSchema
// below) because that constant is defined further down. Same shape, kept in
// sync with the detail-side schema.
const RemittanceListMatchedCustomerSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  /**
   * The name as the source ERP renders it (Famous shows `Acme Foods (ACM)`).
   * Display-only — `name` stays the raw value for sorting and export.
   */
  erpDisplayName: z.string(),
});

export const RemittanceListItemSchema = z.object({
  id: UUIDSchema,
  channel: RemittanceChannelSchema,
  receivedAt: z.string(),
  statedCustomerName: z.string().nullable(),
  statedTotalAmount: DecimalStringSchema.nullable(),
  statedReferenceNo: z.string().nullable(),
  senderEmail: z.string().nullable(),
  /** The cash receipt the remittance is actively linked to (GEN-3202). */
  linkedReceipt: RemittanceLinkedReceiptSchema.nullable(),
  matchedCustomer: RemittanceListMatchedCustomerSchema.nullable(),
  lineItemSummary: RemittanceLineItemSummarySchema,
});

export const PaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

export const ListRemittancesResponseSchema = z.object({
  remittances: z.array(RemittanceListItemSchema),
  pagination: PaginationSchema,
});

// ---------------------------------------------------------------------------
// Get (detail)
// ---------------------------------------------------------------------------

export const GetRemittanceInputSchema = z.object({
  id: UUIDSchema,
});

export const RemittanceLineItemSchema = z.object({
  id: UUIDSchema,
  remittanceAdviceId: UUIDSchema,
  rawInvoiceRef: z.string(),
  invoiceId: UUIDSchema.nullable(),
  statedAmount: DecimalStringSchema.nullable(),
  matchStatus: RemittanceMatchStatusSchema,
  matchReason: z.string().nullable(),
  candidates: z.array(remittanceInvoiceCandidateSchema).default([]),
  kind: RemittanceLineItemKindSchema,
  invoice: RemittanceInvoiceSummarySchema.nullable(),
  /**
   * Collection thread tracking this line, when one has been opened (the
   * invoice's short_pay thread, or the remittance's single deduction thread).
   * `collectionThreadCustomerId` + `collectionThreadId` build the thread-detail
   * route; `collectionThreadRef` is the display code (COL-n).
   */
  collectionThreadId: UUIDSchema.nullable(),
  collectionThreadRef: z.string().nullable(),
  collectionThreadCustomerId: UUIDSchema.nullable(),
  collectionThreadType: z.string().nullable(),
});

export const RemittanceMatchedCustomerSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  /**
   * The name as the source ERP renders it (Famous shows `Acme Foods (ACM)`).
   * Display-only — `name` stays the raw value for sorting and export.
   */
  erpDisplayName: z.string(),
});

/**
 * A customer whose invoices a payment covers — as opposed to the payer who
 * sent the money. Shared by remittance and cash-receipt screens.
 */
export const InvoicedCustomerSchema = RemittanceMatchedCustomerSchema;

export const RemittanceDetailSchema = z.object({
  id: UUIDSchema,
  channel: RemittanceChannelSchema,
  receivedAt: z.string(),
  statedCustomerName: z.string().nullable(),
  statedTotalAmount: DecimalStringSchema.nullable(),
  statedPaymentDate: z.string().nullable(),
  statedPaymentMethod: z.string().nullable(),
  statedReferenceNo: z.string().nullable(),
  senderEmail: z.string().nullable(),
  /** The cash receipt the remittance is actively linked to (GEN-3202). */
  linkedReceipt: RemittanceLinkedReceiptSchema.nullable(),
  /** The payer: the customer on the linked payment. */
  matchedCustomer: RemittanceMatchedCustomerSchema.nullable(),
  /**
   * Whose invoices this remittance pays: the customer of each non-rejected
   * line item's invoice, deduplicated, most cash first (ties: more lines,
   * then name). The payer is not forced
   * in — it appears only when one of its own invoices is on the remittance.
   */
  invoicedCustomers: z.array(InvoicedCustomerSchema),
  /**
   * @deprecated Use `matchedCustomer` (payer) and `invoicedCustomers`. Kept so
   * clients built before the split keep working: the payer first, then
   * `invoicedCustomers`, deduplicated.
   */
  customers: z.array(RemittanceMatchedCustomerSchema),
  /**
   * True when the linked payment has been reconciled against a bank-side
   * cash event (ar_bank_transactions). Indicates the bank has confirmed the
   * funds matching what the customer's remittance advice claimed.
   */
  bankCleared: z.boolean(),
  lineItems: z.array(RemittanceLineItemSchema),
  sourceEmail: InboundEmailSchema.nullable(),
  /**
   * The document the advice was extracted from. The only source to show for
   * channels with no email: a portal download, or a bank memo rendered as text.
   */
  documentId: UUIDSchema.nullable(),
  /**
   * AI overseer result: the check of the extracted advice against its source
   * document (verify_step_output), persisted by the ingestion workflow. Null
   * for advices ingested before verification or when the check was skipped.
   */
  verificationResult: remittanceVerificationResultSchema.nullable(),
});

export const GetRemittanceResponseSchema = z.object({
  remittance: RemittanceDetailSchema.nullable(),
});

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export const ConfirmRemittanceMatchInputSchema = z.object({
  lineItemId: UUIDSchema,
  invoiceId: UUIDSchema,
});

export const ConfirmRemittanceMatchResponseSchema = z.object({
  lineItem: RemittanceLineItemSchema,
  paymentApplicationCreated: z.boolean(),
});

export const RejectLineItemInputSchema = z.object({
  lineItemId: UUIDSchema,
});

export const RejectLineItemResponseSchema = z.object({
  lineItem: RemittanceLineItemSchema,
});

export const UndoRejectLineItemInputSchema = z.object({
  lineItemId: UUIDSchema,
});

export const UndoRejectLineItemResponseSchema = z.object({
  lineItem: RemittanceLineItemSchema,
});

export const LinkEmailInputSchema = z.object({
  remittanceAdviceId: UUIDSchema,
  inboundEmailId: UUIDSchema,
});

export const LinkEmailResponseSchema = z.object({
  success: z.boolean(),
});

export const OverrideRemittanceCustomerInputSchema = z.object({
  remittanceAdviceId: UUIDSchema,
  customerId: UUIDSchema,
});

export const OverrideRemittanceCustomerResponseSchema = z.object({
  remittance: RemittanceDetailSchema,
  /** True when the change was logged for customer learning. */
  correctionRecorded: z.boolean().optional(),
});

export const DeleteRemittanceInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteRemittanceResponseSchema = z.object({
  success: z.literal(true),
});

// --- Manual create / edit ---------------------------------------------------

export const ManualRemittanceLineItemInputSchema = z.object({
  rawInvoiceRef: z.string().trim().min(1),
  statedAmount: DecimalStringSchema.nullable().optional(),
  invoiceId: UUIDSchema.nullable().optional(),
  kind: RemittanceLineItemKindSchema.optional(),
  deductionReason: z.string().nullable().optional(),
});

export const CreateRemittanceInputSchema = z.object({
  customerId: UUIDSchema.nullable().optional(),
  statedCustomerName: z.string().nullable().optional(),
  statedTotalAmount: DecimalStringSchema.nullable().optional(),
  statedPaymentDate: z.string().nullable().optional(),
  statedPaymentMethod: z.string().nullable().optional(),
  statedReferenceNo: z.string().nullable().optional(),
  lineItems: z.array(ManualRemittanceLineItemInputSchema),
});

export const CreateRemittanceResponseSchema = z.object({
  remittance: RemittanceDetailSchema,
});

export const UpdateRemittanceInputSchema = z.object({
  id: UUIDSchema,
  statedCustomerName: z.string().nullable().optional(),
  statedTotalAmount: DecimalStringSchema.nullable().optional(),
  statedPaymentDate: z.string().nullable().optional(),
  statedPaymentMethod: z.string().nullable().optional(),
  statedReferenceNo: z.string().nullable().optional(),
  // Omitted leaves the stored sender untouched; null clears it.
  senderEmail: z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email())
    .nullable()
    .optional(),
});

export const UpdateRemittanceResponseSchema = z.object({
  remittance: RemittanceDetailSchema,
});

/** Reconcile bank cash receipts, then re-run invoice matching with customer memories. */
export const RematchRemittanceInputSchema = z.object({
  id: UUIDSchema,
});

export const RematchRemittanceResponseSchema = z.object({
  remittance: RemittanceDetailSchema,
  outcome: z.enum(["completed", "source_changed", "invoice_matching_failed"]),
  counts: remittanceReprocessCountsSchema,
  /** Null when customer memory was not consulted. */
  memory: remittanceMemorySummarySchema.nullable(),
});

/** Re-run invoice-reference resolution with the current customer memories. */
export const ReprocessRemittanceInputSchema = z.object({
  id: UUIDSchema,
});

export const ReprocessRemittanceResponseSchema = z.object({
  remittance: RemittanceDetailSchema,
  outcome: z.enum(["completed", "source_changed"]),
  counts: remittanceReprocessCountsSchema,
  /** Null when customer memory was not consulted. */
  memory: remittanceMemorySummarySchema.nullable(),
});

/**
 * Bulk add: append one or more line items to an existing remittance. The
 * invoice is chosen via picker (`invoiceId`); `rawInvoiceRef` is derived from
 * the invoice number server-side. `kind` is inferred from the sign of
 * `statedAmount` — a negative amount is a deduction, a positive amount an
 * invoice payment — and the amount is stored as its absolute value, matching
 * the ingest convention (positive amount + explicit `kind`).
 */
export const BulkRemittanceLineItemInputSchema = z.object({
  invoiceId: UUIDSchema,
  statedAmount: DecimalStringSchema.refine((s) => Number(s) !== 0, {
    message: "Amount cannot be zero",
  }),
});

export const AddRemittanceLineItemsInputSchema = z.object({
  remittanceAdviceId: UUIDSchema,
  lineItems: z.array(BulkRemittanceLineItemInputSchema).min(1),
});

export const UpdateRemittanceLineItemInputSchema = z.object({
  lineItemId: UUIDSchema,
  rawInvoiceRef: z.string().trim().min(1).optional(),
  statedAmount: DecimalStringSchema.nullable().optional(),
  invoiceId: UUIDSchema.nullable().optional(),
  kind: RemittanceLineItemKindSchema.optional(),
  deductionReason: z.string().nullable().optional(),
});

export const DeleteRemittanceLineItemInputSchema = z.object({
  lineItemId: UUIDSchema,
});

/** Line-item mutations return the refreshed parent remittance detail. */
export const RemittanceLineItemMutationResponseSchema = z.object({
  remittance: RemittanceDetailSchema,
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const arRemittancesContracts = {
  list: oc
    .input(ListRemittancesInputSchema)
    .output(ListRemittancesResponseSchema)
    .route({
      method: "GET",
      summary: "List remittance advices",
      description:
        "Paginated remittance advices, filterable by review state, channel, received date, stated total, customer, and free text. Each row shows matched customer, linked payment, and line-item match counts.",
    }),
  get: oc
    .input(GetRemittanceInputSchema)
    .output(GetRemittanceResponseSchema)
    .route({
      method: "GET",
      summary: "Get remittance advice details",
      description:
        "Returns one remittance advice with stated header fields, customers, bank-cleared flag, invoice line items with match candidates, source email, and AI verification result.",
    }),
  confirmMatch: oc
    .input(ConfirmRemittanceMatchInputSchema)
    .output(ConfirmRemittanceMatchResponseSchema)
    .route({
      method: "POST",
      summary: "Confirm a remittance line's invoice match",
      description:
        "Marks a remittance line item as matched to a chosen invoice. Applies the payment to the invoice when the payment is already bank-reconciled; otherwise applies later.",
    }),
  rejectLineItem: oc
    .input(RejectLineItemInputSchema)
    .output(RejectLineItemResponseSchema)
    .route({
      method: "POST",
      summary: "Reject a remittance line item",
      description:
        "Marks an unmatched, ambiguous, or auto-matched remittance line item as rejected so it is not applied to any invoice.",
    }),
  undoRejectLineItem: oc
    .input(UndoRejectLineItemInputSchema)
    .output(UndoRejectLineItemResponseSchema)
    .route({
      method: "POST",
      summary: "Undo rejecting a remittance line item",
      description:
        "Restores a rejected remittance line item to an unconfirmed state so it can be matched again.",
    }),
  linkEmail: oc
    .input(LinkEmailInputSchema)
    .output(LinkEmailResponseSchema)
    .route({
      method: "POST",
      summary: "Link an email to a remittance advice",
      description:
        "Manually attaches an ingested inbound email to a remittance advice as its source.",
    }),
  overrideCustomer: oc
    .input(OverrideRemittanceCustomerInputSchema)
    .output(OverrideRemittanceCustomerResponseSchema)
    .route({
      method: "POST",
      summary: "Change the customer on a remittance advice",
      description:
        "Reassigns a remittance advice and its payment to a chosen customer, logs the correction for customer-match learning, then retries bank reconciliation. Returns the refreshed remittance.",
    }),
  delete: oc
    .input(DeleteRemittanceInputSchema)
    .output(DeleteRemittanceResponseSchema)
    .route({
      method: "POST",
      summary: "Delete a remittance advice",
      description:
        "Hard-deletes a remittance with its line items, payment, payment applications, and short-pay threads so ingestion can be re-run. Refused once the payment is bank-cleared.",
    }),
  create: oc
    .input(CreateRemittanceInputSchema)
    .output(CreateRemittanceResponseSchema)
    .route({
      method: "POST",
      summary: "Create a manual remittance advice",
      description:
        "Records a hand-entered remittance with stated customer, total, date, method, reference, and line items. With a customer, also creates and links its payment.",
    }),
  update: oc
    .input(UpdateRemittanceInputSchema)
    .output(UpdateRemittanceResponseSchema)
    .route({
      method: "POST",
      summary: "Edit a remittance advice header",
      description:
        "Corrects stated customer name, total, payment date, method, reference, or sender email on any remittance; changes flow through to its unreconciled payment.",
    }),
  rematch: oc
    .input(RematchRemittanceInputSchema)
    .output(RematchRemittanceResponseSchema)
    .route({
      method: "POST",
      summary: "Re-run matching for a remittance advice",
      description:
        "Reconciles the remittance's payment against bank cash receipts, then re-matches its invoice references using customer memory rules. Returns outcome, counts, and refreshed remittance.",
    }),
  reprocess: oc
    .input(ReprocessRemittanceInputSchema)
    .output(ReprocessRemittanceResponseSchema)
    .route({
      method: "POST",
      summary: "Re-resolve remittance invoice references",
      description:
        "Re-runs invoice-reference matching on unconfirmed lines using current customer memory rules. Confirmed, rejected, and collection-thread lines are left alone. Returns outcome and counts.",
    }),
  addLineItems: oc
    .input(AddRemittanceLineItemsInputSchema)
    .output(RemittanceLineItemMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Add line items to a remittance advice",
      description:
        "Appends invoice lines picked by invoice with amounts; negative amounts become deductions. Returns the refreshed remittance.",
    }),
  updateLineItem: oc
    .input(UpdateRemittanceLineItemInputSchema)
    .output(RemittanceLineItemMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Edit a remittance line item",
      description:
        "Changes invoice reference, amount, invoice, kind, or deduction reason on an unconfirmed line. Assigning an invoice makes it auto-matched pending confirmation.",
    }),
  deleteLineItem: oc
    .input(DeleteRemittanceLineItemInputSchema)
    .output(RemittanceLineItemMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Delete a remittance line item",
      description:
        "Removes a line from a remittance along with any payment applications it created. The correction path for confirmed lines. Returns the refreshed remittance.",
    }),
} as const;

// ---------------------------------------------------------------------------
// Type exports
// ---------------------------------------------------------------------------

export type RemittanceChannel = z.infer<typeof RemittanceChannelSchema>;
export type RemittanceSortBy = z.infer<typeof RemittanceSortBySchema>;
export type RemittanceMatchStatus = z.infer<typeof RemittanceMatchStatusSchema>;
export type RemittanceReviewFilter = z.infer<
  typeof RemittanceReviewFilterSchema
>;
export type RemittanceListItem = z.infer<typeof RemittanceListItemSchema>;
export type RemittanceLineItemSummary = z.infer<
  typeof RemittanceLineItemSummarySchema
>;
export type ListRemittancesInput = z.infer<typeof ListRemittancesInputSchema>;
export type ListRemittancesResponse = z.infer<
  typeof ListRemittancesResponseSchema
>;
export type RemittanceDetail = z.infer<typeof RemittanceDetailSchema>;
export type RemittanceMatchedCustomer = z.infer<
  typeof RemittanceMatchedCustomerSchema
>;
export type InvoicedCustomer = z.infer<typeof InvoicedCustomerSchema>;
export type RemittanceLineItem = z.infer<typeof RemittanceLineItemSchema>;
export type ConfirmRemittanceMatchInput = z.infer<
  typeof ConfirmRemittanceMatchInputSchema
>;
export type ConfirmRemittanceMatchResponse = z.infer<
  typeof ConfirmRemittanceMatchResponseSchema
>;
export type RematchRemittanceInput = z.infer<
  typeof RematchRemittanceInputSchema
>;
export type ReprocessRemittanceInput = z.infer<
  typeof ReprocessRemittanceInputSchema
>;
export type ReprocessRemittanceResponse = z.infer<
  typeof ReprocessRemittanceResponseSchema
>;
export type OverrideRemittanceCustomerInput = z.infer<
  typeof OverrideRemittanceCustomerInputSchema
>;
export type OverrideRemittanceCustomerResponse = z.infer<
  typeof OverrideRemittanceCustomerResponseSchema
>;
export type RemittanceLineItemKind = z.infer<
  typeof RemittanceLineItemKindSchema
>;
export type ManualRemittanceLineItemInput = z.infer<
  typeof ManualRemittanceLineItemInputSchema
>;
export type CreateRemittanceInput = z.infer<typeof CreateRemittanceInputSchema>;
export type CreateRemittanceResponse = z.infer<
  typeof CreateRemittanceResponseSchema
>;
export type UpdateRemittanceInput = z.infer<typeof UpdateRemittanceInputSchema>;
export type AddRemittanceLineItemsInput = z.infer<
  typeof AddRemittanceLineItemsInputSchema
>;
export type UpdateRemittanceLineItemInput = z.infer<
  typeof UpdateRemittanceLineItemInputSchema
>;
export type DeleteRemittanceLineItemInput = z.infer<
  typeof DeleteRemittanceLineItemInputSchema
>;
